import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
} from "@mui/material";

import SpeedIcon from "@mui/icons-material/Speed";

import '../../assets/concept-tags.css'
// Custom component for speech synthesis
const SpeechSynthesisAudioComponent = ({ speechSynthData }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const speeds = [0.5, 0.75, 1];

  // Animated Speaker Component
  const AnimatedSpeakerIcon = ({ isPlaying }) => {
    return (
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="64" 
        height="64" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="white" 
        strokeWidth="2"
      >
        {/* Speaker Body */}
        <path d="M12 6 L7 10 L3 10 L3 14 L7 14 L12 18 Z" fill="white" />
        
        {/* Sound Waves - Always present, animated only when playing */}
        <path 
          d="M15 8 Q17 10, 17 12 Q17 14, 15 16" 
          strokeLinecap="round" 
          fill="none" 
          opacity={isPlaying ? 1 : 0.3}
          style={{ 
            animation: isPlaying ? 'wave1 1s infinite' : 'none',
            transformOrigin: 'center'
          }} 
        />
        <path 
          d="M17 6 Q20 10, 20 12 Q20 15, 17 18" 
          strokeLinecap="round" 
          fill="none" 
          opacity={isPlaying ? 1 : 0.3}
          style={{ 
            animation: isPlaying ? 'wave2 1s infinite 0.3s' : 'none',
            transformOrigin: 'center'
          }} 
        />
      </svg>
    );
  };

  useEffect(() => {
    // Set up event listener for speech end
    if (speechSynthData) {
      const handleSpeechEnd = () => {
        setIsPlaying(false);
      };
      
      speechSynthData.utterance.onend = handleSpeechEnd;
      
      return () => {
        speechSynthData.utterance.onend = null;
      };
    }
  }, [speechSynthData]);

  const handleSpeakerClick = () => {
    if (speechSynthData) {
      if (!isPlaying) {
        // Set the speech rate based on playback speed
        speechSynthData.utterance.rate = playbackSpeed;
        speechSynthData.play();
        setIsPlaying(true);
      } else {
        speechSynthData.stop();
        setIsPlaying(false);
      }
    }
  };

  const handleSpeedChange = () => {
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
    
    // If currently playing, update the rate
    if (isPlaying && speechSynthData) {
      speechSynthData.stop();
      speechSynthData.utterance.rate = nextSpeed;
      speechSynthData.play();
    }
  };

  // New useEffect to add keyboard event listener for "p" key or Caps Lock key
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if pressed key is "p" (case-insensitive) or "CapsLock"
      if (event.key.toLowerCase() === "p" || event.key === "CapsLock") {
        handleSpeakerClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [speechSynthData, isPlaying, playbackSpeed]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        padding: "0% 1% 0% 1%",
        borderRadius: '10%',
      }}
    >
      <style>
        {`
        @keyframes wave1 {
            0%, 100% { opacity: 0.5; transform: scaleX(0.8); }
            50% { opacity: 1; transform: scaleX(1); }
        }
        @keyframes wave2 {
            0%, 100% { opacity: 0.5; transform: scaleX(0.8); }
            50% { opacity: 1; transform: scaleX(1); }
        }
        `}
      </style>

      <Box
        sx={{
          borderRadius: '50%',
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'background-color 0.3s',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)'
          },
        }}
        onClick={handleSpeakerClick}
      >
        <AnimatedSpeakerIcon isPlaying={isPlaying} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)'
          }, 
          px: 1,
          py: 0.5,
          mt: -2
        }}
        onClick={handleSpeedChange}
      >
        <SpeedIcon
          sx={{
            color: 'white',
            fontSize: '1rem',
            mr: 0.5,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: 'white',
            fontWeight: 'bold'
          }}
        >
          {playbackSpeed}x
        </Typography>
      </Box>
    </Box>
  );
};

export default SpeechSynthesisAudioComponent;
